import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import useOrderPackage from '../../../../hooks/useOrderPackage';
import useGiftsPageAddons from '../../../../hooks/useGiftsPageAddons';
import useHolidayPricelist from '../../../../hooks/useHolidayPricelist';
import useHolidayPoses from '../../../../hooks/useHolidayPoses';
import useBackgrounds from '../../../../hooks/useBackgrounds';
import useSiblingImages from '../../../../hooks/useSiblingImages';
import { useCustomer } from '../../../../contexts/CustomerContext';
import { addonSheetToSheet } from '../../../PackageBuilders/MultiPose/functions';
import Builder from './Builder';
import Sheet from '../../../../models/Sheet';
import routes from '../../../../routes';
import { AddonSheet } from '../../../../models';
import useQueryString from '../../../../hooks/use-query-string';
import QUERY_KEYS from '../../../../utils/QueryKeys';
import { sheetUnitPrice } from '../../../../utils/functions';
import useRetouchingExtra from "../../../../hooks/useRetouchingExtra";
import useMultiSeasonPoses from "../../../../hooks/useMultiSeasonPoses";
import usePoses from "../../../../hooks/usePoses";
import useOfferCoupon from "../../../../hooks/useOfferCoupon";
import useCart from "../../../../hooks/useCart";

export default function Edit() {
  const { s: initS } = useQueryString();
  const history = useHistory();
  const { orderPackageId } = useParams();
  const { customer, setCustomer } = useCustomer();
  const { data: orderPackage, refetch, isLoadingOp } = useOrderPackage(orderPackageId);

  // The live offer for this student, scoped to the order package's pricelist
  // (packageId == the senior pid 745). Lets the review modal reflect a free
  // announcement bundle instead of the list price.
  const { data: offer } = useOfferCoupon(
    customer?.currentStudent?.studentId,
    customer?.currentSchool?.id,
    orderPackage?.packageId,
  );

  // Server-computed: does the cart hold a senior portraits package > $1? The
  // ReviewModal's subtotal must respect this or an announcements-only cart
  // would show "Subtotal: $0.00" and mismatch what checkout charges.
  // refetchOnMount:'always' because the app-wide QueryClient default is
  // refetchOnMount:false and nothing invalidates [QUERY_KEYS.CART,...] when
  // packages are added on other pages.
  const { data: cartQualification } = useCart(customer?.cart?.id, { refetchOnMount: 'always' });
  const qualifiesForFreeAnnouncements = !!cartQualification?.qualifiesForFreeAnnouncements;
  const { data: addons } = useGiftsPageAddons();
  const { data: pricelist } = useHolidayPricelist(customer?.currentSchool?.id);
  const { data: poses, isLoadingPoses } = orderPackage?.packageId && (orderPackage?.package?.multipleSeasons?.length || orderPackage?.package?.displaySharedPoses) ? (
    useMultiSeasonPoses(customer?.currentStudent?.id, orderPackage?.packageId)
  ) : (
    usePoses(customer.currentStudent.id, orderPackage?.packageId)
  );
  const { data: backgrounds } = useBackgrounds(pricelist?.id, ['productTypeNames']);
  const { data: retouching } = useRetouchingExtra();

  const queryClient = useQueryClient();

  useEffect(() => refetch(), []);

  const s = [initS].flat() || [];

  const [wantsMoreItems, setWantsMoreItems] = useState(false);

  const option = orderPackage?.apiOption;

  const {
    isLoading,
    mutate,
  } = useMutation({
    mutationKey: ['orderPackage', orderPackageId, 'mutate'],
    mutationFn: async ({ sheets, additionalPoses, wantsMoreItems }) => {
      const oldSheets = orderPackage.addonSheets.map((sheet) => {
        sheet.isMarkedForDestruction = true;

        return sheet;
      });

      orderPackage.addonSheets = [...oldSheets];

      try {
        // try deleting, if already deleted just try continuing
        await orderPackage.save({ with: 'addonSheets' });
      } catch (e) {
        console.log(e);
      }

      const newSheets = sheets.map((sheetParams) => {
        const addon = addons.find((cur) => cur.id.toString() === sheetParams.imageType.id.toString());

        return new AddonSheet({ id: undefined, ...sheetParams, addon, addonId: addon.id });
      });

      orderPackage.addonSheets = [...newSheets];

      await refetch();

      await orderPackage.save({ with: 'addonSheets' });

      return { wantsMoreItems };
    },
    onSuccess: async (data) => {
      if (Object.keys(orderPackage.errors).length > 0) return;

      setCustomer({ ...customer, currentOrderPackage: orderPackage });

      if (data.wantsMoreItems) {
        await queryClient.invalidateQueries([QUERY_KEYS.ORDER_PACKAGES]).then(() => {
          history.push(routes.holiday.builder({ o: orderPackageId, pid: orderPackage?.packageId }));
        });
      } else {
        await queryClient.invalidateQueries([QUERY_KEYS.ORDER_PACKAGES]).then(() => {
          if (retouching) {
            history.push(routes.retouching(orderPackageId))
          } else {
            history.push(routes.review());
          }
        });
      }
    },
  });

  // Calculate subtotal to present during review
  const subtotal = useMemo(() => {
    if (!orderPackage) return 0;
    if (!option) return 0;

    // sheetUnitPrice: server-stamped snapshot, shared fallback rule (keyed on
    // the ORDER PACKAGE price, like the charge — the old inline rule keyed on
    // the option price and could disagree with checkout).
    return option.prices[0].price + orderPackage.addonSheets.reduce((acc, cur) => (
      acc + sheetUnitPrice(cur, orderPackage)
    ), 0);
  }, [!!orderPackage, !!option]);

  // UI Hooks
  const isFetching = useIsFetching();

  useEffect(() => {
    document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'none'; });

    return () => document.querySelectorAll('.rails-nav').forEach((node) => { node.style.display = 'flex'; });
  }, []);

  if (!orderPackage) return null;
  if (isFetching) return null;
  if (backgrounds.length === 0) return null;
  if (isLoadingOp) return null;
  if (isLoadingPoses) return null;

  const mapped = s.filter((a) => !!a).map((a) => a.toString());

  const sheetsToEdit = orderPackage
    .addonSheets
    .filter((ads) => mapped.length === 0 || mapped.includes(ads.id.toString()));

  return (
    <Builder
      backgrounds={backgrounds}
      initialSheets={sheetsToEdit.map((addonSheet) => addonSheetToSheet(addonSheet))}
      reviewSheets={orderPackage.addonSheets.map((addonSheet) => addonSheetToSheet(addonSheet))}
      orderPackageId={orderPackageId}
      orderPackage={orderPackage}
      poses={poses}
      isLoading={isLoading}
      errors={orderPackage.errors}
      option={{ ...(pricelist.options[0] || {}), poses: Number.POSITIVE_INFINITY, prices: [{ price: subtotal }] }}
      packageId={orderPackage.packageId}
      offer={offer}
      qualifiesForFreeAnnouncements={qualifiesForFreeAnnouncements}
      onConfirm={(sheets, additionalPoses, wantsMoreItems) => mutate({ sheets, additionalPoses, wantsMoreItems })}
    />
    // <Builder
    //   backgrounds={backgrounds}
    //   imageTypes={orderPackage.addonSheets.map((addonSheet) => ({ name: addonSheet.addon.name, id: addonSheet.addon.id }))}
    //   initialSheets={orderPackage.addonSheets.map((addonSheet) => addonSheetToSheet(addonSheet))}
    //   isLoading={isLoading}
    //   option={{ ...(option || {}), poses: Number.POSITIVE_INFINITY, prices: [{ price: subtotal }] }}
    //   packageId={orderPackage.packageId}
    //   poses={posesForPackage}
    //   onConfirm={(sheets) => mutate({ sheets })}
    // />
  );
}
